import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { TRAINING_PROGRAMS } from "@/data/content";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Award,
  Briefcase,
  BookOpen,
  Users,
  Download,
  Send,
  Calendar,
  GraduationCap,
} from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useContent } from "@/hooks/useContent";

const emptyForm = {
  full_name: "",
  email: "",
  phone: "",
  program: "",
  institution: "",
  education_level: "",
  mode: "",
  message: "",
};

const features = [
  { Icon: BookOpen, t: "Course Catalog", d: "Structured, hands-on programs across engineering & science." },
  { Icon: Award, t: "Certification", d: "Industry-recognized certifications on program completion." },
  { Icon: Briefcase, t: "Internships", d: "Live industrial projects and research internships." },
  { Icon: GraduationCap, t: "Final Year Projects", d: "Guided FYP with real-world outcomes." },
  { Icon: Users, t: "Research Mentorship", d: "Mentored by researchers and engineers." },
  { Icon: Calendar, t: "Workshops & Summer Training", d: "Short intensives and summer research schools." },
];

const Training = () => {
  useDocumentTitle("Training");
  const { items: PROGRAMS } = useContent("training-programs", TRAINING_PROGRAMS);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.program) {
      toast.error("Please choose a program.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/training/apply", form);
      toast.success("Application received. We'll contact you soon.");
      setForm(emptyForm);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Please check the form.");
    } finally {
      setLoading(false);
    }
  };

  const downloadBrochure = () => {
    const content = `AKYVIA SCIENCES AND TECHNOLOGIES\nTraining Programs Brochure\n\n${PROGRAMS.map((p) => `• ${p.name} — ${p.duration} — ${p.level}`).join("\n")}\n\nContact: akyvia.st@gmail.com`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Akyvia-Training-Brochure.txt";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Brochure downloaded.");
  };

  return (
    <div data-testid="training-page">
      <PageHeader
        eyebrow="Student Training"
        title="Hands-on training in the frontiers of engineering & science."
        description="Structured programs, certifications, workshops and research fellowships across AI, robotics, motors, aerospace and space sciences."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Training" }]}
      />

      {/* Programs */}
      <section className="py-20 lg:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-medium mb-3">
                Program catalog
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D] tracking-tight">
                Choose your specialization.
              </h2>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                data-testid="training-download-brochure"
                onClick={downloadBrochure}
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0B1B3D] text-slate-700 hover:text-[#0B1B3D] px-5 py-3 rounded-md font-medium transition-colors"
              >
                <Download className="w-4 h-4" strokeWidth={1.5} />
                Download Brochure
              </button>
              <a
                href="#apply"
                data-testid="training-apply-cta"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0B1B3D] font-semibold px-5 py-3 rounded-md transition-colors"
              >
                Apply for Training
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROGRAMS.map((p) => (
              <div
                key={p.id || p.name}
                data-testid={`training-program-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:border-sky-300 transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-600 font-medium">
                      {p.tag}
                    </div>
                    <h3 className="mt-1 font-display text-lg font-semibold text-[#0B1B3D]">
                      {p.name}
                    </h3>
                  </div>
                  <div className="w-9 h-9 rounded-md border border-slate-200 flex items-center justify-center">
                    <GraduationCap className="w-4 h-4 text-[#0B1B3D]" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="mt-5 border-t border-slate-100 pt-4 flex items-center justify-between text-xs text-slate-600">
                  <span className="font-mono-akyvia">{p.duration}</span>
                  <span>{p.level}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-white to-sky-50/40 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D] tracking-tight max-w-2xl">
            More than a course. A launchpad.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map(({ Icon, t, d }) => (
              <div
                key={t}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:border-sky-300 transition-colors"
              >
                <Icon className="w-5 h-5 text-amber-500 mb-3" strokeWidth={1.5} />
                <div className="font-display font-semibold text-[#0B1B3D]">{t}</div>
                <p className="text-sm text-slate-600 leading-relaxed mt-1.5">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" className="py-20 lg:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-medium mb-3">
              Apply now
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D] tracking-tight leading-tight">
              Start your Akyvia training journey.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Fill in the form — our academic team will review and get in touch
              with next steps, schedule and fee information.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="training-application-form"
            className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 grid gap-5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tr-name">Full Name*</Label>
                <Input
                  id="tr-name"
                  data-testid="training-name-input"
                  required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="tr-email">Email*</Label>
                <Input
                  id="tr-email"
                  data-testid="training-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="tr-phone">Phone*</Label>
                <Input
                  id="tr-phone"
                  data-testid="training-phone-input"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="tr-inst">Institution</Label>
                <Input
                  id="tr-inst"
                  data-testid="training-institution-input"
                  value={form.institution}
                  onChange={(e) => setForm({ ...form, institution: e.target.value })}
                />
              </div>

              <div>
                <Label>Program*</Label>
                <Select
                  value={form.program}
                  onValueChange={(v) => setForm({ ...form, program: v })}
                >
                  <SelectTrigger data-testid="training-program-select">
                    <SelectValue placeholder="Choose a program" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROGRAMS.map((p) => (
                      <SelectItem key={p.id || p.name} value={p.name}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Education Level</Label>
                <Select
                  value={form.education_level}
                  onValueChange={(v) => setForm({ ...form, education_level: v })}
                >
                  <SelectTrigger data-testid="training-edu-select">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {["Diploma", "Undergraduate", "Postgraduate", "PhD", "Working Professional"].map(
                      (l) => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Preferred Mode</Label>
                <Select
                  value={form.mode}
                  onValueChange={(v) => setForm({ ...form, mode: v })}
                >
                  <SelectTrigger data-testid="training-mode-select">
                    <SelectValue placeholder="Online / Offline / Hybrid" />
                  </SelectTrigger>
                  <SelectContent>
                    {["Online", "Offline", "Hybrid"].map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="tr-msg">Anything you'd like to share?</Label>
              <Textarea
                id="tr-msg"
                data-testid="training-message-input"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="training-submit-button"
              className="inline-flex items-center justify-center gap-2 bg-[#0B1B3D] hover:bg-[#1E3A8A] disabled:opacity-60 text-white font-medium px-6 py-3.5 rounded-md"
            >
              <Send className="w-4 h-4" />
              {loading ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        </div>
      </section>

      <CTASection
        eyebrow="Corporate Training"
        title="Interested in corporate or institutional training?"
        description="We design custom training programs for engineering colleges and industry teams."
      />
      <div className="py-24" />
    </div>
  );
};

export default Training;
